import { useEffect, useState, useCallback, useRef } from 'react';
import { Node, Edge, ReactFlowProvider, useReactFlow } from 'reactflow';
import { MeterLatest, DeviceState, CatalogDevice, InstallationData } from '../../api/architecture';
import { TelemetryData } from '../../api/wattelEM';
import { ElectricalSchemaFlow } from './ElectricalSchemaFlow';
import { SynopticView } from './SynopticView';
import { useI18n } from '../../i18n';
import { formatPower } from '../../utils/power';
import { Btn, LegendChip } from '../ui/Button';
import { IconPlus, IconEye, IconPencil, IconTrash, IconRotate } from '../ui/icons';

export type ConsultationViewMode = 'schema' | 'synoptic';

/** Switch Schéma | Synoptique (bandeau de consultation). */
function ViewModeSwitch({ value, onChange }: { value: ConsultationViewMode; onChange: (m: ConsultationViewMode) => void }) {
  const { t } = useI18n();
  const opt = (m: ConsultationViewMode, label: string, icon: JSX.Element) => (
    <button
      type="button"
      role="tab"
      aria-selected={value === m}
      onClick={() => onChange(m)}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all touch-manipulation ${
        value === m ? 'bg-white text-zce-navy shadow' : 'text-white/80 hover:text-white hover:bg-white/10'
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
  return (
    <div role="tablist" aria-label={t('arch_display_type')} className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/15 border border-white/25 backdrop-blur">
      {opt('schema', t('arch_tab_schema'), (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <rect x="3" y="4" width="6" height="6" rx="1.5" /><rect x="15" y="4" width="6" height="6" rx="1.5" /><rect x="9" y="14" width="6" height="6" rx="1.5" />
          <path d="M6 10v2h12v-2M12 12v2" />
        </svg>
      ))}
      {opt('synoptic', t('arch_tab_synoptic'), (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M3 12h4l2-6 4 12 2-6h6" />
        </svg>
      ))}
    </div>
  );
}

interface ConsultationSchemaViewProps {
  nodes: Node[];
  edges: Edge[];
  devices?: DeviceState[];
  catalogDevices?: CatalogDevice[];
  meterData?: MeterLatest[];
  wattelemTelemetry?: Record<string, TelemetryData>;
  installationName?: string;
  onSwitchToEdit?: () => void;
  viewMode?: ConsultationViewMode;
  onChangeViewMode?: (mode: ConsultationViewMode) => void;
  isVerticalView?: boolean;
  onToggleVerticalView?: () => void;
  onTableauClick?: (tableauNode: Node) => void; // Callback pour clic sur tableau → navigation WattelEM
  onPointClick?: (pointNode: Node) => void; // Callback pour clic sur point électrique → navigation WattelEM
  onWattelemClick?: (sn: string) => void; // Callback pour clic sur un WattelEM précis → navigation équipement
  installations?: InstallationData[];
  selectedInstallationId?: number | null;
  onSelectInstallation?: (id: number) => void;
  onCreateInstallation?: () => void;
  onViewInstallation?: (id: number) => void;
  onEditInstallation?: (id: number) => void;
  onDeleteInstallation?: (id: number) => void;
}

// Auto-layout function with intelligent spacing based on content
function autoLayoutNodes(inputNodes: Node[], inputEdges: Edge[]): Node[] {
  console.log('[AutoLayout] Starting with nodes:', inputNodes.length, 'edges:', inputEdges.length);
  
  // Deep clone nodes with proper spread
  const nodes = inputNodes.map(n => ({ 
    ...n,
    data: { ...n.data },
    position: { x: 0, y: 0 }
  }));
  
  // Build parent-child relationships from edges
  const parentMap = new Map<string, string>();
  inputEdges.forEach(e => parentMap.set(e.target, e.source));
  
  // Find nodes by type
  const linkyNodes = nodes.filter(n => n.type === 'linky' || n.type === 'edf' || n.type === 'triphase' || n.type === 'souscompteur');
  const tableauNodes = nodes.filter(n => n.type === 'tableau');
  const zoneNodes = nodes.filter(n => n.type === 'zone');
  const pointNodes = nodes.filter(n => n.type === 'point');
  
  console.log('[AutoLayout] Found:', { linky: linkyNodes.length, tableau: tableauNodes.length, zones: zoneNodes.length, points: pointNodes.length });
  
  // Layout configuration
  const startX = 80;
  const baseY = 300;
  const colGap = 450;
  const minPointGap = 250; // Minimum spacing between equipment
  
  // Column 0: Linky/Compteurs
  linkyNodes.forEach((linky, index) => {
    linky.position = { x: startX, y: baseY + index * 250 };
  });
  
  // Column 1: Tableaux
  const tableauY = linkyNodes.length > 0 ? linkyNodes[0].position.y - 50 : baseY;
  tableauNodes.forEach((tableau, index) => {
    tableau.position = { x: startX + colGap, y: tableauY + index * 350 };
  });
  
  // Column 2: Zones - INTELLIGENT SPACING
  // Group points by parent to calculate required space per zone
  const pointsByParent = new Map<string, Node[]>();
  pointNodes.forEach(point => {
    const parentId = parentMap.get(point.id);
    if (parentId) {
      if (!pointsByParent.has(parentId)) {
        pointsByParent.set(parentId, []);
      }
      pointsByParent.get(parentId)!.push(point);
    }
  });
  
  // Calculate required height for each zone
  const zoneHeights = new Map<string, number>();
  zoneNodes.forEach(zone => {
    const children = pointsByParent.get(zone.id) || [];
    const requiredHeight = children.length > 0 ? children.length * minPointGap : 100;
    zoneHeights.set(zone.id, requiredHeight);
  });
  
  // Position zones with cumulative spacing
  const tableauCenterY = tableauY + 175;
  let currentY = tableauCenterY;
  
  if (zoneNodes.length === 1) {
    const zone = zoneNodes[0];
    zone.position = { x: startX + colGap * 2, y: tableauCenterY - 40 };
  } else if (zoneNodes.length > 0) {
    const totalHeight = Array.from(zoneHeights.values()).reduce((sum, h) => sum + h, 0);
    currentY = tableauCenterY - totalHeight / 2;
    
    zoneNodes.forEach((zone) => {
      const zoneHeight = zoneHeights.get(zone.id) || 100;
      zone.position = { x: startX + colGap * 2, y: currentY };
      currentY += zoneHeight;
    });
  }
  
  // Column 3: Points - INTELLIGENT SPACING
  pointsByParent.forEach((points, parentId) => {
    const parentNode = nodes.find(n => n.id === parentId);
    if (parentNode && points.length > 0) {
      const parentCenterY = parentNode.position.y + 40;
      const totalHeight = (points.length - 1) * minPointGap;
      const startPointY = parentCenterY - totalHeight / 2;
      
      points.forEach((point, idx) => {
        point.position = {
          x: startX + colGap * 3,
          y: startPointY + idx * minPointGap
        };
      });
    }
  });
  
  console.log('[AutoLayout] Intelligent layout applied');
  
  return nodes;
}

// Floating controls component for tablet touch interaction
function FloatingControls({ onFitView }: { onFitView: () => void }) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const { t } = useI18n();
  
  const handleFitView = useCallback(() => {
    fitView({ padding: 0.15, duration: 300 });
    onFitView();
  }, [fitView, onFitView]);

  const handleZoomIn = useCallback(() => {
    zoomIn({ duration: 200 });
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut({ duration: 200 });
  }, [zoomOut]);

  return (
    <div className="absolute bottom-6 right-6 z-50 flex flex-col gap-2">
      {/* Zoom controls - large buttons for touch */}
      <button
        onClick={handleZoomIn}
        className="w-14 h-14 bg-white hover:bg-blue-50 active:bg-blue-100 rounded-xl shadow-lg border border-slate-200 flex items-center justify-center text-2xl text-slate-700 transition-all active:scale-95 touch-manipulation"
        title={t('arch_zoom_in')}
        aria-label={t('arch_zoom_in')}
      >
        +
      </button>
      <button
        onClick={handleZoomOut}
        className="w-14 h-14 bg-white hover:bg-blue-50 active:bg-blue-100 rounded-xl shadow-lg border border-slate-200 flex items-center justify-center text-2xl text-slate-700 transition-all active:scale-95 touch-manipulation"
        title={t('arch_zoom_out')}
        aria-label={t('arch_zoom_out')}
      >
        −
      </button>
      <div className="h-1"></div>
      {/* Fit/Center button */}
      <button
        onClick={handleFitView}
        className="w-14 h-14 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl shadow-lg flex items-center justify-center text-xl text-white transition-all active:scale-95 touch-manipulation"
        title={t('arch_recenter')}
        aria-label={t('arch_recenter')}
      >
        ⊡
      </button>
    </div>
  );
}

// Inner component that uses ReactFlow hooks
function ConsultationSchemaViewInner({
  layoutedNodes,
  edges,
  devices,
  catalogDevices,
  meterData,
  wattelemTelemetry,
  installationName,
  currentTime,
  stats,
  onSwitchToEdit,
  viewMode,
  onChangeViewMode,
  isVerticalView,
  onToggleVerticalView,
  onTableauClick,
  onPointClick,
  onWattelemClick,
  installations,
  selectedInstallationId,
  onSelectInstallation,
  onCreateInstallation,
  onViewInstallation,
  onEditInstallation,
  onDeleteInstallation,
}: {
  layoutedNodes: Node[];
  edges: Edge[];
  devices: DeviceState[];
  catalogDevices: CatalogDevice[];
  meterData: MeterLatest[];
  wattelemTelemetry: Record<string, TelemetryData>;
  installationName: string;
  currentTime: Date;
  stats: { zones: number; points: number; online: number; power: string };
  onSwitchToEdit?: () => void;
  viewMode: ConsultationViewMode;
  onChangeViewMode?: (mode: ConsultationViewMode) => void;
  isVerticalView?: boolean;
  onToggleVerticalView?: () => void;
  onTableauClick?: (tableauNode: Node) => void;
  onPointClick?: (pointNode: Node) => void;
  onWattelemClick?: (sn: string) => void;
  installations?: InstallationData[];
  selectedInstallationId?: number | null;
  onSelectInstallation?: (id: number) => void;
  onCreateInstallation?: () => void;
  onViewInstallation?: (id: number) => void;
  onEditInstallation?: (id: number) => void;
  onDeleteInstallation?: (id: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t, locale } = useI18n();

  // Dummy callback for fit view
  const handleFitViewDone = useCallback(() => {
    console.log('[ConsultationView] FitView done');
  }, []);

  return (
    <div className="h-screen w-full flex flex-col bg-zce-gray-light">
      {/* ZCE Header - Hero animé premium */}
      <div className="relative overflow-hidden shadow-2xl flex-shrink-0 zce-fade-in">
        <div className="absolute inset-0 bg-gradient-to-br from-zce-navy-dark via-zce-navy to-zce-navy-light zce-gradient-anim" />
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-cyan-500/20 blur-3xl zce-float-orb" />
        <div className="pointer-events-none absolute -bottom-32 -right-16 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl zce-float-orb" style={{ animationDelay: '-6s' }} />
        <div className="pointer-events-none absolute top-1/2 left-1/3 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl zce-float-orb" style={{ animationDelay: '-12s' }} />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="pointer-events-none absolute inset-0 zce-shimmer" />

        <div className="relative px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 zce-fade-up">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/40 rounded-xl blur-md zce-pulse-dot" />
                <img
                  src="/logos/logo zce.png"
                  alt={t('arch3_logo_alt')}
                  className="relative h-12 w-auto object-contain bg-white rounded-xl p-1.5 shadow-lg"
                />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-heading tracking-tight">
                  <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">{installationName}</span>
                </h1>
                <div className="flex items-center gap-3 text-sm text-blue-200/80">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full zce-pulse-dot"></span>
                    {t('arch_view_mode')}
                  </span>
                  <span>•</span>
                  <span className="tabular-nums">{currentTime.toLocaleTimeString(locale)}</span>
                </div>
              </div>
            </div>

            {/* Stats - ZCE style */}
            <div className="hidden md:flex items-center gap-3 zce-fade-up zce-delay-1">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl px-4 py-2 zce-card-hover">
                <span className="text-xs text-white/70 block uppercase tracking-wider">{t('arch_power')}</span>
                <span className="text-lg font-extrabold text-zce-orange tabular-nums">{stats.power}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl px-4 py-2 zce-card-hover">
                <span className="text-xs text-white/70 block uppercase tracking-wider">{t('arch_zones')}</span>
                <span className="text-lg font-extrabold text-white tabular-nums">{stats.zones}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl px-4 py-2 zce-card-hover">
                <span className="text-xs text-white/70 block uppercase tracking-wider">{t('arch_points')}</span>
                <span className="text-lg font-extrabold text-white tabular-nums">{stats.points}</span>
              </div>
            </div>

            {/* Action buttons - ZCE style */}
            <div className="flex items-center gap-3 zce-fade-up zce-delay-2">
              {onChangeViewMode && layoutedNodes.length > 0 && (
                <ViewModeSwitch value={viewMode} onChange={onChangeViewMode} />
              )}
              {onSwitchToEdit && (
                <Btn variant="primaryOnDark" onClick={onSwitchToEdit} icon={<IconPencil />}>
                  <span className="hidden sm:inline">{t('arch_edit_short')}</span>
                </Btn>
              )}
              {/* Toggle Mode Portrait (pour tablette tenue verticalement) — schéma uniquement */}
              {onToggleVerticalView && viewMode === 'schema' && (
                <Btn variant="onDark" active={isVerticalView} onClick={onToggleVerticalView} title={isVerticalView ? t('arch_landscape_mode') : t('arch_portrait_mode')} icon={<IconRotate />}>
                  <span className="hidden sm:inline">{isVerticalView ? t('arch_landscape') : t('arch_portrait')}</span>
                </Btn>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Installation Selector */}
      {onSelectInstallation && (
        <div className="bg-gradient-to-r from-slate-50 to-white border-b border-gray-200 px-6 py-2.5 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Left: Select + New button */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-zce-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <select
                  value={selectedInstallationId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) onSelectInstallation(Number(val));
                  }}
                  className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-zce-navy bg-white focus:ring-2 focus:ring-zce-blue focus:border-zce-blue outline-none min-w-[250px] cursor-pointer"
                >
                  <option value="">{t('arch_select_installation')}</option>
                  {(installations || []).map(inst => (
                    <option key={inst.id} value={inst.id}>
                      {inst.name} {inst.status === 'production' ? '🟢' : inst.status === 'validated' ? '🔵' : '⚪'}
                    </option>
                  ))}
                </select>
              </div>
              {onCreateInstallation && (
                <Btn variant="primary" size="sm" onClick={onCreateInstallation} title={t('arch_new_installation')} icon={<IconPlus />}>
                  <span className="hidden sm:inline">{t('arch_new_short')}</span>
                </Btn>
              )}
              <span className="text-xs text-gray-400 hidden md:inline">{(() => { const n = (installations || []).length; return t(n === 1 ? 'arch_installations_count_one' : 'arch_installations_count_other', { n }); })()}</span>
            </div>

            {/* Right: Action buttons for selected installation */}
            {selectedInstallationId && (
              <div className="flex items-center gap-2">
                {onViewInstallation && (
                  <Btn size="sm" onClick={() => onViewInstallation(selectedInstallationId)} title={t('arch_view_installation')} icon={<IconEye />}>
                    <span className="hidden sm:inline">{t('arch_view')}</span>
                  </Btn>
                )}
                {onEditInstallation && (
                  <Btn size="sm" onClick={() => onEditInstallation(selectedInstallationId)} title={t('arch_edit_installation')} icon={<IconPencil />}>
                    <span className="hidden sm:inline">{t('arch_edit')}</span>
                  </Btn>
                )}
                {onDeleteInstallation && (
                  <Btn variant="danger" size="sm" onClick={() => onDeleteInstallation(selectedInstallationId)} title={t('arch_delete_installation')} icon={<IconTrash />}>
                    <span className="hidden sm:inline">{t('arch_delete')}</span>
                  </Btn>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schema Legend - ZCE style */}
      <div className="bg-white border-b border-gray-100 px-6 py-2 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-zce-navy rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          <h2 className="text-sm font-semibold text-zce-navy">{viewMode === 'synoptic' ? t('arch_synoptic_title') : t('arch_schema_title')}</h2>
        </div>
        {viewMode === 'synoptic' ? (
          <span className="text-xs text-gray-500 hidden sm:inline">{t('arch_synoptic_hint')}</span>
        ) : (
        <div className="flex gap-2">
          <LegendChip tone="amber">Linky</LegendChip>
          <LegendChip tone="slate">{t('arch_legend_tableau')}</LegendChip>
          <LegendChip tone="cyan">{t('arch_legend_zone')}</LegendChip>
          <LegendChip tone="rose">{t('arch_legend_point')}</LegendChip>
        </div>
        )}
      </div>

      {/* Schema container with floating controls */}
      <div
        ref={containerRef}
        className={`flex-1 relative ${viewMode === 'synoptic' ? 'overflow-y-auto' : 'touch-pan-x touch-pan-y'}`}
      >
        {layoutedNodes.length > 0 && viewMode === 'synoptic' ? (
          <SynopticView
            embedded
            nodes={layoutedNodes}
            edges={edges}
            devices={devices}
            meterData={meterData}
            wattelemTelemetry={wattelemTelemetry}
            installationName={installationName}
            onTableauClick={onTableauClick}
            onPointClick={onPointClick}
          />
        ) : layoutedNodes.length > 0 ? (
          <>
            <ElectricalSchemaFlow
              editable={false}
              initialNodesOverride={layoutedNodes}
              initialEdgesOverride={edges}
              devices={devices}
              catalogDevices={catalogDevices}
              meterData={meterData}
              wattelemTelemetry={wattelemTelemetry}
              isVerticalLayout={isVerticalView}
              onTableauClick={onTableauClick}
              onPointClick={onPointClick}
              onWattelemClick={onWattelemClick}
            />
            
            {/* Floating touch controls */}
            <FloatingControls onFitView={handleFitViewDone} />
            
            {/* Touch hint - shows briefly */}
            <div className="absolute bottom-6 left-6 z-40 bg-black/60 text-white text-xs px-3 py-2 rounded-lg pointer-events-none opacity-70">
              👆 {t('arch_touch_hint')}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center max-w-md mx-auto p-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-zce-blue/10 to-cyan-500/10 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-zce-blue/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('arch_no_installation_selected')}</h3>
              <p className="text-sm text-slate-400">{t('arch_no_installation_hint')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ConsultationSchemaView({
  nodes,
  edges,
  devices = [],
  catalogDevices = [],
  meterData = [],
  wattelemTelemetry = {},
  installationName,
  onSwitchToEdit,
  viewMode = 'schema',
  onChangeViewMode,
  isVerticalView = false,
  onToggleVerticalView,
  onTableauClick,
  onPointClick,
  onWattelemClick,
  installations,
  selectedInstallationId,
  onSelectInstallation,
  onCreateInstallation,
  onViewInstallation,
  onEditInstallation,
  onDeleteInstallation,
}: ConsultationSchemaViewProps) {
  const { t, locale } = useI18n();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [layoutedNodes, setLayoutedNodes] = useState<Node[]>([]);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Apply auto-layout once when nodes change
  useEffect(() => {
    if (nodes.length > 0) {
      console.log('[ConsultationView] Input nodes:', nodes.map(n => ({ id: n.id, type: n.type, pos: n.position })));
      console.log('[ConsultationView] Edges:', edges.length);
      const laid = autoLayoutNodes(nodes, edges);
      console.log('[ConsultationView] Layouted nodes:', laid.map(n => ({ id: n.id, type: n.type, pos: n.position })));
      setLayoutedNodes(laid);
      console.log('[ConsultationView] Layout applied!');
    }
  }, [nodes, edges]);

  // Calculate stats safely
  // Puissance = somme des WattelEM des tableaux (compteurs principaux), EN LIGNE uniquement.
  // Les prises connectées en aval ne sont pas ajoutées (elles sont déjà mesurées par le WattelEM),
  // et la dernière valeur d'un appareil hors ligne n'est pas une puissance actuelle.
  const wemSns = new Set(nodes
    .filter(n => n.type === 'tableau')
    .flatMap(n => (Array.isArray(n.data?.wattelemList) ? n.data.wattelemList : []) as Array<{ sn?: string }>)
    .map(w => String(w.sn ?? '').toLowerCase())
    .filter(Boolean));
  const wattelemPowerKw = Object.entries(wattelemTelemetry || {}).reduce((sum, [sn, tel]) => {
    if (!wemSns.has(sn.toLowerCase())) return sum;
    const st = devices.find(d => d.deviceSn.toLowerCase() === sn.toLowerCase());
    if (st && !st.online) return sum;
    return sum + (Number(tel?.power_kw) || 0);
  }, 0);
  const stats = {
    zones: nodes.filter(n => n.type === 'zone').length,
    points: nodes.filter(n => n.type === 'point').length,
    online: devices.filter(d => d.online).length,
    power: formatPower(wattelemPowerKw, locale), // « 3 W » sous 1 kW, sinon en kW
  };

  if (layoutedNodes.length === 0 && nodes.length > 0) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">{t('arch_loading_schema')}</p>
        </div>
      </div>
    );
  }

  // Wrap in ReactFlowProvider for touch controls to work
  return (
    <ReactFlowProvider>
      <ConsultationSchemaViewInner
        layoutedNodes={layoutedNodes}
        edges={edges}
        devices={devices}
        catalogDevices={catalogDevices}
        meterData={meterData}
        wattelemTelemetry={wattelemTelemetry}
        installationName={installationName || t('arch_default_installation_name')}
        currentTime={currentTime}
        stats={stats}
        onSwitchToEdit={onSwitchToEdit}
        viewMode={viewMode}
        onChangeViewMode={onChangeViewMode}
        isVerticalView={isVerticalView}
        onToggleVerticalView={onToggleVerticalView}
        onTableauClick={onTableauClick}
        onPointClick={onPointClick}
        onWattelemClick={onWattelemClick}
        installations={installations}
        selectedInstallationId={selectedInstallationId}
        onSelectInstallation={onSelectInstallation}
        onCreateInstallation={onCreateInstallation}
        onViewInstallation={onViewInstallation}
        onEditInstallation={onEditInstallation}
        onDeleteInstallation={onDeleteInstallation}
      />
    </ReactFlowProvider>
  );
}

export default ConsultationSchemaView;
